import React, {useEffect, useRef, useState} from "react";
import styled, {css} from "styled-components";
import {useCheckMobileView} from "helpers";

const WhatWeBelieve = ({believeData = {}}) => {
  const {title = "", subtitle = "", beliefs = []} = believeData;
  const [openIdx, setOpenIdx] = useState(0);
  const isMobileView = useCheckMobileView();

  return (
    <Section>
      <Title>{title}</Title>
      <Subtitle>
        {subtitle}
      </Subtitle>
      <BeliefsContainer>
        <CardsGrid>
          {beliefs.map((belief, idx) => (
            <BeliefCard
              key={idx}
              role="button"
              tabIndex={0}
              $open={openIdx === idx}
              onClick={() => setOpenIdx(idx)}
              onFocus={() => setOpenIdx(idx)}
              onMouseEnter={() => setOpenIdx(idx)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpenIdx(idx); } }}
            >
              <IconWrapper $open={openIdx === idx}>
                <img
                  alt={belief.title}
                  src={belief.icon}
                  width={112}
                  height={112}
                  decoding="async"
                />
              </IconWrapper>

              <MobileCardTitle $open={openIdx === idx}>{belief.title}</MobileCardTitle>

              <ClosedContent $open={openIdx === idx}>
                <VerticalText>{belief.title}</VerticalText>
              </ClosedContent>

              <OpenContent $open={openIdx === idx}>
                {belief.image && (
                  <MainImage
                    alt={belief.title}
                    src={isMobileView ? belief.mobileImage : belief.image}
                    width={isMobileView ? 1120 : 920}
                    height={isMobileView ? 560 : 600}
                    loading="lazy"
                    decoding="async"
                  />
                )}
                <ContentText>
                  <CardTitle>{belief.title}</CardTitle>
                  <CardDesc>{belief.description}</CardDesc>
                </ContentText>
              </OpenContent>
            </BeliefCard>
          ))}
        </CardsGrid>
      </BeliefsContainer>
    </Section>
  );
};

const Section = styled.section`
  min-height: 54rem;
  margin-bottom: 5rem;
  margin-top: 5rem;
  padding: 4.8rem 0 0 0;

  @media (max-width: 768px) {
    padding: 2.66rem 0 0 0;
    margin-bottom: 0;
    margin-top: 0;
  }

  @media (max-width: 1100px) {
    padding: 2.66rem 0 0 0;
    margin-bottom: 0;
    margin-top: 0;
  }
`;

const Title = styled.h2`
  font-family: 'DM Serif Display';
  font-weight:400;
  font-size: 5rem;
  color: #1C1C1C;
  text-align: center;
  margin-bottom: 1.8rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    font-size: 2.7rem;
  }

  @media (max-width: 1100px) {
    font-size: 2.7rem;
  }
`;

const Subtitle = styled.p`
  font-size: 2rem;
  color: #6F6F6F;
  font-family: 'Lato';
  font-weight: 400;
  text-align: center;
  max-width: 70rem;
  margin: 0 auto 3.6rem auto;
  line-height: 3.2rem;

  @media (max-width: 768px) {
    font-size: 1.4rem;
    max-width: 90%;
  }

  @media (max-width: 1100px) {
    font-size: 1.4rem;
    max-width: 90%;
  }
`;

const BeliefsContainer = styled.div`
  width: 85%;
  max-width: 125rem;
  margin: 0 auto;
  overflow: hidden;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4rem;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const BeliefCard = styled.div`
  background: #F9F6FF;
  border-radius: 16px;
  min-height: 42rem;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  outline: none;
  border: none;
  font: inherit;
  text-align: left;
  padding: 0;

  width: ${({$open}) => ($open ? "85rem" : "")};
  max-width: ${({$open}) => ($open ? "85rem" : "25rem")};

  transition:
    width 1s cubic-bezier(0.35, .9, 0.35, .9),
    max-width 1s cubic-bezier(0.45, 1.1, 0.45, .9),
    box-shadow 1s cubic-bezier(0.25, 0.8, 0.25, 1);

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
    min-height: 39rem;
    height: auto;
    padding: 5.55rem 1.77rem 3rem 0.77rem;
    display: flex;
    align-items: center;
    justify-content: center;
    padding-top: 2rem;
  }

  @media (max-width: 1100px) {
    width: 100%;
    max-width: 100%;
    min-height: 39rem;
    height: auto;
    padding: 5.55rem 1.77rem 3rem 0.77rem;
    display: flex;
    align-items: center;
    justify-content: center;
    padding-top: 2rem;
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  top: 2.4rem;
  z-index: 10;

  left: ${({$open}) => ($open ? "5rem" : "50%")};
  transform: ${({$open}) => ($open ? "none" : "translateX(-50%)")};

  img {
    width: 7rem;
    height: 7rem;
    object-fit: contain;
  }

  @media (max-width: 768px) {
    display: none;
  }

  @media (max-width: 1100px) {
    display: none;
  }
`;

const MobileCardTitle = styled.h2`
  display: none;

  @media (max-width: 768px) {
  display:none;
  }
`;

const ClosedContent = styled.div`
  position: absolute;
  bottom: 3.2rem;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  height: calc(100% - 10rem);

  opacity: ${({$open}) => ($open ? 0 : 1)};
  transform: ${({$open}) => ($open ? "translateY(-2rem)" : "translateY(0)")};
  pointer-events: ${({$open}) => ($open ? "none" : "auto")};

  transition:
    opacity 0.5s cubic-bezier(0.25, 0.8, 0.25, 1),
    transform 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);

  transition-delay: ${({$open}) => ($open ? "0s" : "0.15s")};

  @media (max-width: 768px) {
    display: none;
  }
`;

const OpenContent = styled.div`
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #F9F6FF;
  padding: 0 0 2rem 5rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  height: 52rem;

  opacity: ${({$open}) => ($open ? 1 : 0)};
  transform: ${({$open}) => ($open ? "translateY(0)" : "translateY(2rem)")};
  pointer-events: ${({$open}) => ($open ? "auto" : "none")};

  transition:
    opacity 0.5s cubic-bezier(0.25, 0.8, 0.25, 1),
    transform 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);

  transition-delay: ${({$open}) => ($open ? "0.15s" : "0s")};

  @media (max-width: 768px) {
    position: static;
    padding: 0;
    align-items: center;
    justify-content: center;
    opacity: 1;
    transform: none;
    pointer-events: auto;
    background: transparent;
    height: auto;
  }

  @media (max-width: 1100px) {
    position: static;
    padding: 0;
    align-items: center;
    justify-content: center;
    opacity: 1;
    transform: none;
    pointer-events: auto;
    background: transparent;
    height: auto;
  }
`;

const MainImage = styled.img`
  width: 50rem;
  height: 28rem;
  object-fit: contain;
  align-self: flex-end;

  opacity: 0;
  transform: scale(0.8) translateY(2rem);
  animation: imageEntrance 0.8s cubic-bezier(0.25, 0.8, 0.25, 1) forwards;
  animation-delay: 0.5s;

  @media (max-width: 768px) {
    width: 80%;
    max-width: 24.44rem;
    align-self: center;
    margin-right: 0;
  }

  @media (max-width: 1100px) {
    width: 80%;
    max-width: 24.44rem;
    align-self: center;
    margin-right: 0;
  }
`;

const ContentText = styled.div`
  opacity: 0;
  transform: translateY(2rem);
  animation: textEntrance 0.6s cubic-bezier(0.25, 0.8, 0.25, 1) forwards;
  animation-delay: 0.6s;
`;

const CardTitle = styled.h2`
  font-size: 3rem;
  font-family: 'Playfair Display';
  font-weight: 700;
  margin: 0 0 0.8rem 0;
  color: #1C1C1C;

  @media (max-width: 768px) {
    font-family: 'DM Serif Display';
    font-size: 2.3rem;
    text-align: center;
  }

  @media (max-width: 1100px) {
    font-family: 'DM Serif Display';
    font-size: 2.3rem;
    text-align: center;
  }
`;

const CardDesc = styled.p`
  font-size: 2rem;
  font-family:'Lato';
  color: #6F6F6F;
  font-weight:400;
  margin: 0;
  line-height: 1.4;
  max-width: 48rem;

  @media (max-width: 768px) {
    font-size: 1.33rem;
    max-width: 100%;
    text-align: center;
    margin-bottom: 20px;
  }

  @media (max-width: 1100px) {
    font-size: 1.33rem;
    max-width: 100%;
    text-align: center;
    margin-bottom: 20px;
  }
`;

const VerticalText = styled.div`
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  font-size: 2.4rem;
  font-family: 'Lato';
  color: #1C1C1C;
  letter-spacing: 0.2rem;
  text-align: center;
  font-weight: 400;

  opacity: 0;
  animation: verticalTextEntrance 0.6s cubic-bezier(0.25, 0.8, 0.25, 1) forwards;
  animation-delay: 0.3s;

  @media (max-width: 768px) {
    writing-mode: unset;
    transform: unset;
    font-size: 1.77rem;
    color: #888;
    font-weight: 500;
    text-align: center;
    width: 100%;
    padding: 1.8rem 0;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
  }

  @media (max-width: 1100px) {
    writing-mode: unset;
    transform: unset;
    font-size: 1.77rem;
    color: #888;
    font-weight: 500;
    text-align: center;
    width: 100%;
    padding: 1.8rem 0;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
  }
`;

const GlobalStyles = `
  @keyframes imageEntrance {
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  @keyframes textEntrance {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes verticalTextEntrance {
    to {
      opacity: 1;
    }
  }
`;

if (typeof document !== "undefined") {
  const styleSheet = document.createElement("style");
  styleSheet.textContent = GlobalStyles;
  document.head.appendChild(styleSheet);
}
export default WhatWeBelieve;